import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { IssueState } from '../../api/areas/registry';
import { FilterBar, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useApi } from '../../lib/useApi';
import {
  enumLabel,
  FilterSelect,
  ISSUE_STATE,
  IssueTable,
  RefreshButton,
  RegistrySelect,
  ResetButton,
  TransactionTable,
  UnknownOutcomeCallout,
  useParamSetter,
  useRegistryCrumbs,
} from './shared';

const ISSUE_TYPES = ['EXISTENCE', 'SPONSORSHIP', 'EXPIRY', 'STATUS', 'CONTACT', 'NAMESERVER', 'DNSSEC', 'TRANSFER', 'UNKNOWN_OUTCOME'];

/** `/registry/reconciliation`: unconfirmed outcomes and differences against the registry (spec §29–§30, ADR-008). */
export function ReconciliationPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('reconciliation.title'));
  const crumbs = useRegistryCrumbs();
  const [params, set, reset] = useParamSetter();
  const registry = params.get('registry') ?? '';
  const state = (params.get('state') ?? '') as IssueState | '';
  const issueType = params.get('issue_type') ?? '';
  const result = useApi(
    (signal) => api.getReconciliationQueue({ registry: registry || undefined, state: state || undefined, issue_type: issueType || undefined, limit: 200 }, { signal }),
    [registry, state, issueType],
  );

  return (
    <>
      <PageHeader title={t('reconciliation.title')} crumbs={crumbs} subtitle={t('reconciliation.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <div className="space-y-6">
        <UnknownOutcomeCallout tone="info" link={false} />
        <FilterBar actions={<ResetButton onClick={reset} disabled={!registry && !state && !issueType} />}>
          <RegistrySelect value={registry} onChange={(v) => set('registry', v)} />
          <FilterSelect
            label={t('filter.state')}
            value={state}
            onChange={(v) => set('state', v)}
            anyLabel={t('reconciliation.anyOpen')}
            className="min-w-48"
            options={Object.keys(ISSUE_STATE).map((s) => ({ value: s, label: enumLabel('issueState', s) }))}
          />
          <FilterSelect
            label={t('filter.type')}
            value={issueType}
            onChange={(v) => set('issue_type', v)}
            className="min-w-44"
            options={ISSUE_TYPES.map((s) => ({ value: s, label: enumLabel('issueType', s) }))}
          />
        </FilterBar>
        {result.status === 'loading' ? <Loading rows={4} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('reconciliation.loadError')} /> : null}
        {result.status === 'success' ? (
          <>
            <Section
              flush
              className={result.data.unresolved_transactions.length > 0 ? 'border-serious/40' : undefined}
              title={t('reconciliation.unknownTitle')}
              description={t('reconciliation.unknownDesc', { count: result.data.unresolved_transactions.length })}
            >
              {result.data.unresolved_transactions.length === 0 ? (
                <div className="px-5 pb-5">
                  <EmptyState title={t('reconciliation.noUnknown')} />
                </div>
              ) : (
                <TransactionTable rows={result.data.unresolved_transactions} />
              )}
            </Section>
            <Section flush title={t('reconciliation.issuesTitle')} description={t('reconciliation.issuesDesc', { count: result.data.issues.length })}>
              <IssueTable rows={result.data.issues} empty={<EmptyState title={t('reconciliation.noIssues')} />} />
            </Section>
          </>
        ) : null}
      </div>
    </>
  );
}
